/**
 * Permission queries for TanStack Query (Vendor App)
 */

import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet } from '../utils/request'

// Type definitions
export interface PermissionBase {
  id: number
  resource: string | null
  action: string | null
  resource_description: string | null
  action_description: string | null
}

export interface PermissionListItem extends PermissionBase {
  type: 'primary' | 'default' | 'vendor'
  depends_on: PermissionBase[]
}

export interface PaginatedPermissionsResponse {
  items: PermissionListItem[]
  total: number | null
  limit: number
  offset: number
  has_more: boolean
}

export interface PermissionListParams {
  type?: 'primary' | 'default' | 'vendor'
  limit?: number
  offset?: number
  [key: string]: any
}

// Cache configuration
const CACHE_TIMES = {
  PERMISSIONS: 10 * 60 * 1000, // 10 minutes - permissions rarely change
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch all permissions
 */
export const usePermissionsQuery = (params: MaybeRefOrGetter<PermissionListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.permissionList(toValue(params))),
    queryFn: async (): Promise<PaginatedPermissionsResponse> => {
      return queryGet<PaginatedPermissionsResponse>('/v2/permissions/', { params: toValue(params) })
    },
    staleTime: CACHE_TIMES.PERMISSIONS,
  })
}
